import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { allQuests, artists } from "@/mocks/artistData";

const colorMap: Record<string, string> = {
  cyan: "from-[#4fc3f7]/20 to-transparent border-[#4fc3f7]/30 text-[#4fc3f7]",
  magenta: "from-[#ff6eb4]/20 to-transparent border-[#ff6eb4]/30 text-[#ff6eb4]",
  yellow: "from-[#ffd60a]/20 to-transparent border-[#ffd60a]/30 text-[#ffd60a]",
  green: "from-[#a8e063]/20 to-transparent border-[#a8e063]/30 text-[#a8e063]",
  orange: "from-[#ff8c42]/20 to-transparent border-[#ff8c42]/30 text-[#ff8c42]",
  purple: "from-[#c77dff]/20 to-transparent border-[#c77dff]/30 text-[#c77dff]",
};

const badgeColor: Record<string, string> = {
  cyan: "bg-[#4fc3f7]/10 text-[#4fc3f7]",
  magenta: "bg-[#ff6eb4]/10 text-[#ff6eb4]",
  yellow: "bg-[#ffd60a]/10 text-[#ffd60a]",
  green: "bg-[#a8e063]/10 text-[#a8e063]",
  orange: "bg-[#ff8c42]/10 text-[#ff8c42]",
  purple: "bg-[#c77dff]/10 text-[#c77dff]",
};

const questTypeIcon: Record<string, string> = {
  mv: "ri-film-line",
  dance: "ri-music-2-line",
  scene: "ri-tv-2-line",
  poster: "ri-image-line",
  trailer: "ri-movie-2-line",
  challenge: "ri-trophy-line",
};

const questTypeLabel: Record<string, string> = {
  mv: "뮤직비디오",
  dance: "안무",
  scene: "드라마 신",
  poster: "포스터",
  trailer: "예고편",
  challenge: "챌린지",
};

const difficultyLabel: Record<string, string> = {
  Easy: "입문",
  Medium: "중급",
  Hard: "고급",
};

const categoryColorMap: Record<string, string> = {
  kpop: "#ff6eb4",
  drama: "#3d9be9",
  film: "#e84545",
  webtoon: "#ff8c42",
};

const categoryLabelMap: Record<string, string> = {
  kpop: "K-POP",
  drama: "드라마",
  film: "영화",
  webtoon: "웹툰",
};

const filterTabs = [
  { type: "all", label: "전체", icon: "ri-apps-line" },
  { type: "kpop", label: "K-POP", icon: "ri-music-2-line" },
  { type: "drama", label: "드라마", icon: "ri-tv-2-line" },
  { type: "film", label: "영화", icon: "ri-movie-2-line" },
  { type: "webtoon", label: "웹툰", icon: "ri-book-open-line" },
];

export default function LatestQuestSection() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const navigate = useNavigate();

  const filteredQuests = allQuests
    .filter((q) => {
      if (activeFilter === "all") return true;
      const artist = artists.find((a) => a.id === q.artistId);
      return artist?.category === activeFilter;
    })
    .slice(0, 6);

  return (
    <section className="py-20 px-6 bg-[#07070e]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-r from-[#ff4757] to-[#ffd60a]">
                <i className="ri-fire-fill text-white text-xs" />
              </span>
              <span className="text-[#ff4757] text-xs font-semibold tracking-widest uppercase">Latest Campaigns</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              지금 바로 참여할 수 있는<br />
              <span className="bg-gradient-to-r from-[#ff6eb4] via-[#3d9be9] to-[#ff8c42] bg-clip-text text-transparent">최신 엔터테인먼트 캠페인</span>
            </h2>
          </div>
          <Link
            to="/quests"
            className="hidden md:flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            전체 캠페인 <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex items-center gap-3 mb-8 flex-wrap">
          {filterTabs.map((tab) => (
            <button
              key={tab.type}
              onClick={() => setActiveFilter(tab.type)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                activeFilter === tab.type
                  ? "bg-white/10 border-white/30 text-white"
                  : "border-white/10 text-white/50 hover:text-white/70 hover:border-white/20"
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Quest Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredQuests.map((quest) => {
            const artist = artists.find((a) => a.id === quest.artistId);
            const colors = colorMap[quest.color] || colorMap.cyan;
            const badge = badgeColor[quest.color] || badgeColor.cyan;
            const isHovered = hoveredId === quest.id;
            const typeIcon = questTypeIcon[quest.questType] || "ri-film-line";
            const typeLabel = questTypeLabel[quest.questType] || "캠페인";
            const catColor = artist ? categoryColorMap[artist.category] : "#ff6eb4";
            const catLabel = artist ? categoryLabelMap[artist.category] : "";

            return (
              <div
                key={quest.id}
                className={`relative rounded-2xl overflow-hidden border bg-gradient-to-b ${colors} bg-[#0f0f1a] cursor-pointer transition-all duration-300 ${isHovered ? "scale-[1.02]" : ""}`}
                onMouseEnter={() => setHoveredId(quest.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => navigate(`/quests/${quest.id}`)}
              >
                {/* Thumbnail */}
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={quest.thumbnail}
                    alt={quest.title}
                    className={`w-full h-full object-cover object-top transition-transform duration-500 ${isHovered ? "scale-110" : "scale-100"}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f1a] via-transparent to-transparent" />

                  {/* Category Badge */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <span
                      className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: `${catColor}20`, color: catColor, border: `1px solid ${catColor}40` }}
                    >
                      {catLabel}
                    </span>
                    <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${badge}`}>
                      <i className={typeIcon} />
                      {typeLabel}
                    </span>
                  </div>

                  {/* Reward */}
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1">
                    <i className="ri-coin-line text-[#ffd60a] text-xs" />
                    <span className="text-white text-xs font-bold">{quest.reward.toLocaleString()} XP</span>
                  </div>

                  {/* Difficulty */}
                  <div className="absolute bottom-3 left-3">
                    <span className="text-white/60 text-[10px] bg-black/50 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      {"★".repeat(quest.difficultyStars)}{"☆".repeat(5 - quest.difficultyStars)} {difficultyLabel[quest.difficulty]}
                    </span>
                  </div>

                  {/* Play overlay */}
                  {isHovered && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30">
                        <i className="ri-play-fill text-white text-xl ml-0.5" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  {/* Artist/Creator */}
                  {artist && (
                    <div className="flex items-center gap-2 mb-2.5">
                      <img src={artist.avatar} alt={artist.name} className="w-5 h-5 rounded-full object-cover object-top" />
                      <span className="text-white/50 text-xs">{artist.name}</span>
                      <span className="text-white/30 text-xs">·</span>
                      <span className="text-white/30 text-xs">{artist.groupName}</span>
                      {artist.verified && <i className="ri-verified-badge-fill text-xs" style={{ color: artist.accentColor }} />}
                    </div>
                  )}

                  <h3 className="text-white font-semibold text-sm mb-1.5 line-clamp-1">{quest.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed line-clamp-2 mb-3">{quest.description}</p>

                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/40 text-xs">참여율</span>
                      <span className="text-white/60 text-xs font-medium">{quest.progress}%</span>
                    </div>
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${quest.progress}%`,
                          background: `linear-gradient(90deg, ${artist?.accentColor || "#ff6eb4"}, ${artist?.secondaryColor || "#ffd6e8"})`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="flex -space-x-1.5">
                        {quest.participantAvatars.slice(0, 3).map((av, i) => (
                          <img key={i} src={av} alt="" className="w-5 h-5 rounded-full border border-[#0f0f1a] object-cover object-top" />
                        ))}
                      </div>
                      <span className="text-white/40 text-xs">{quest.participants.toLocaleString()}명</span>
                    </div>
                    <div className="flex items-center gap-1 text-white/40 text-xs">
                      <i className="ri-time-line text-xs" />
                      {quest.deadline}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/quests"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-sparkling-line" />
            모든 캠페인 보기
          </Link>
        </div>
      </div>
    </section>
  );
}
